import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import ObjectTableHeaderRow from 'src/components/ObjectTableHeaderRow/ObjectTableHeaderRow'
import SortableTableHeader, {
  SortableTableHeaderAlignment,
} from 'src/components/SortableTableHeader/SortableTableHeader'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import Trns from 'src/localization/Trns'
import { BudgetRequestListModel } from 'src/models/budgetRequest.models'
import { updateBudgetRequestUrl } from 'src/navigation/urls'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { css } from 'styled-components'

interface BudgetRequestsTableLargeProps {
  budgetRequests: BudgetRequestListModel[]
  isApprover?: boolean
}

const BudgetRequestsTableLarge: React.FC<BudgetRequestsTableLargeProps> = (
  props,
): React.ReactElement => {
  const users = useSelector(selectUserListModelsFromState)

  return (
    <ObjectTable>
      <ObjectTableHeaderRow>
        {props.isApprover && (
          <SortableTableHeader
            css={css`
              width: 15%;

              ${getBreakpointDown(breakpointSizes.MD)(css`
                width: 25%;
              `)};
            `}
            alignment={SortableTableHeaderAlignment.LeftAligned}
            label={<Trns source={'Date of Request'} />}
          />
        )}
        <SortableTableHeader
          css={css`
            width: 24%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 25%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={<Trns source={'Name'} />}
        />
        <SortableTableHeader
          css={css`
            width: 24%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 25%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={<Trns source={props.isApprover ? 'User' : 'Approver'} />}
        />
        <SortableTableHeader
          css={css`
            width: 12%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 0%;
              overflow: hidden;
              pointer-events: none;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={<Trns source={'Type'} />}
        />
        {props.isApprover && (
          <SortableTableHeader
            css={css`
              width: 10%;

              ${getBreakpointDown(breakpointSizes.MD)(css`
                width: 0%;
                overflow: hidden;
                pointer-events: none;
              `)};
            `}
            alignment={SortableTableHeaderAlignment.LeftAligned}
            label={<Trns source={'Active Until'} />}
          />
        )}
        <SortableTableHeader
          css={css`
            width: 12%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 21%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
          label={<Trns source={'Limit'} />}
        />
        <SortableTableHeader
          css={css`
            width: 4%;
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
        />
      </ObjectTableHeaderRow>
      {props.budgetRequests.map((budgetRequest, i) => {
        const user = props.isApprover
          ? budgetRequest.requester
          : budgetRequest.approver
        const displayUser = users[user.id] || user

        return (
          <ObjectTableBodyRow
            to={updateBudgetRequestUrl(budgetRequest.id)}
            key={i}
            ariaLabelSource={'Account Overview'}
          >
            {props.isApprover && (
              <ObjectTableBodyCell
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {budgetRequest.updatedAtTimezonedString}
              </ObjectTableBodyCell>
            )}
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                overflow: visible;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <img
                  css={css`
                    height: 20px;
                  `}
                  src={budgetRequest.budgetTypeImageSrc}
                  alt={''}
                />
                <span
                  css={css`
                    margin-left: 6px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {budgetRequest.displayName}
                </span>
              </div>
            </ObjectTableBodyCell>
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <UserAvatarAndName user={displayUser} />
            </ObjectTableBodyCell>
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {budgetRequest.recurs ? (
                <Trns source={'Recurring'} />
              ) : (
                <Trns source={'Standard'} />
              )}
            </ObjectTableBodyCell>
            {props.isApprover && (
              <ObjectTableBodyCell
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {budgetRequest.recurs ? (
                  <Trns source={'See Request Details'} />
                ) : (
                  <>{budgetRequest.untilTimezonedString}</>
                )}
              </ObjectTableBodyCell>
            )}
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
                text-align: right;
              `}
            >
              {budgetRequest.issuedAmountLabel}
            </ObjectTableBodyCell>
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                overflow: visible;
              `}
            >
              <AnimatedTableChevron
                css={css`
                  margin-top: -4px;
                `}
              />
            </ObjectTableBodyCell>
          </ObjectTableBodyRow>
        )
      })}
    </ObjectTable>
  )
}

export default BudgetRequestsTableLarge
